/* ============================================================================
   useMagneticHover.ts — hook React pour effet "magnetic" cursor → élément.

   Quand le curseur entre dans un rayon configurable autour du centre de
   l'élément, celui-ci se translate vers le curseur (effet aimant) avec une
   force proportionnelle à la proximité. Sortie du rayon → retour à 0 via
   lerp doux (rubber band).

   Issu du scan CX 2026-05-12 TOP 10 #9 (ICE 18.7) — pattern Codrops Magnetic
   Buttons adapté à la typographie poétique MARTHE.

   Usage :
     const ref = useRef<HTMLElement>(null);
     const style = useMagneticHover(ref, { radius: 120, strength: 0.25 });
     return <article ref={ref} style={style}>...</article>;

   Perf : un seul listener `mousemove` global partagé (pattern singleton),
   chaque hook lit la dernière position via ref. RAF par hook actif pour
   lerp smooth. Cleanup auto.
   ============================================================================ */

import { useEffect, useRef, useState } from 'react';
import type { CSSProperties, RefObject } from 'react';

interface Options {
  /** Rayon d'attraction en px depuis le centre de l'élément. Default 120. */
  radius?: number;
  /** Force max d'attraction (0..1). 0.25 = 25% de la distance vers cursor. */
  strength?: number;
  /** Smoothing factor 0..1. Plus haut = plus rapide. Default 0.18. */
  lerp?: number;
  /** Disable totalement (e.g. sur mobile touch). */
  disabled?: boolean;
}

// Singleton mouse tracker — un seul listener pour tous les hooks actifs
let mouseX = -9999;
let mouseY = -9999;
let listenerCount = 0;

function trackMouse(e: MouseEvent) {
  mouseX = e.clientX;
  mouseY = e.clientY;
}

function attachListener() {
  if (listenerCount === 0) {
    document.addEventListener('mousemove', trackMouse, { passive: true });
  }
  listenerCount++;
}

function detachListener() {
  listenerCount = Math.max(0, listenerCount - 1);
  if (listenerCount === 0) {
    document.removeEventListener('mousemove', trackMouse);
    mouseX = -9999;
    mouseY = -9999;
  }
}

export function useMagneticHover<T extends HTMLElement>(
  ref: RefObject<T | null>,
  options: Options = {},
): CSSProperties {
  const {
    radius = 120,
    strength = 0.25,
    lerp = 0.18,
    disabled = false,
  } = options;

  const [transform, setTransform] = useState<string>('translate3d(0,0,0)');
  const currentRef = useRef({ x: 0, y: 0 });
  const targetRef = useRef({ x: 0, y: 0 });
  const rafRef = useRef<number | null>(null);

  useEffect(() => {
    if (disabled) return;
    attachListener();

    const step = () => {
      const el = ref.current;
      if (!el) {
        rafRef.current = requestAnimationFrame(step);
        return;
      }
      const rect = el.getBoundingClientRect();
      const cx = rect.left + rect.width / 2;
      const cy = rect.top + rect.height / 2;
      const dx = mouseX - cx;
      const dy = mouseY - cy;
      const dist = Math.hypot(dx, dy);

      if (dist < radius) {
        // Force linéaire : 1 au centre, 0 au radius
        const force = 1 - dist / radius;
        targetRef.current.x = dx * force * strength;
        targetRef.current.y = dy * force * strength;
      } else {
        targetRef.current.x = 0;
        targetRef.current.y = 0;
      }

      // Lerp smooth
      const cur = currentRef.current;
      const tgt = targetRef.current;
      cur.x += (tgt.x - cur.x) * lerp;
      cur.y += (tgt.y - cur.y) * lerp;

      // Skip update si quasi-stable (< 0.1 px) pour économie de re-renders
      if (Math.abs(cur.x) < 0.1 && Math.abs(cur.y) < 0.1 && Math.abs(tgt.x) < 0.1 && Math.abs(tgt.y) < 0.1) {
        setTransform('translate3d(0,0,0)');
      } else {
        setTransform(`translate3d(${cur.x.toFixed(2)}px, ${cur.y.toFixed(2)}px, 0)`);
      }

      rafRef.current = requestAnimationFrame(step);
    };

    rafRef.current = requestAnimationFrame(step);

    return () => {
      if (rafRef.current != null) cancelAnimationFrame(rafRef.current);
      detachListener();
    };
  }, [ref, radius, strength, lerp, disabled]);

  return {
    transform,
    // willChange aide GPU à optimiser le transform (compositor layer)
    willChange: disabled ? 'auto' : 'transform',
  };
}
